import axios from 'axios';

// Debug environment variables
console.log('Admin API Configuration:');
console.log('REACT_APP_ADMIN_API_BASE_URL:', process.env.REACT_APP_ADMIN_API_BASE_URL);
console.log('REACT_APP_DEPLOYMENT_ENV:', process.env.REACT_APP_DEPLOYMENT_ENV);
console.log('NODE_ENV:', process.env.NODE_ENV);

// Get base URL with fallback
const getBaseURL = () => {
  const envBaseURL = process.env.REACT_APP_ADMIN_API_BASE_URL;
  
  // First priority: environment variable
  if (envBaseURL && envBaseURL.trim() !== '') {
    console.log('Using environment API URL:', envBaseURL);
    return envBaseURL.endsWith('/') ? envBaseURL.slice(0, -1) : envBaseURL;
  }
  
  // Second priority: fallback based on deployment environment
  const isDevelopment = process.env.REACT_APP_DEPLOYMENT_ENV === 'development' || 
                       process.env.NODE_ENV === 'development';
  
  const fallbackURL = isDevelopment 
    ? 'https://admin-api-test.any-ship.com'
    : 'https://admin-api.any-ship.com';
  
  console.warn('Environment API URL not set, using fallback:', fallbackURL);
  return fallbackURL;
};

const baseURL = getBaseURL();
console.log('Final API Base URL:', baseURL);

// Create axios instance for admin API
const apiClient = axios.create({
  baseURL: baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to log requests
apiClient.interceptors.request.use(
  (config) => {
    console.log('Making API request to:', config.baseURL + config.url);
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const adminApi = {
  // Settings
  getSettings: async () => {
    return await apiClient.get('/api/admin/settings');
  },

  updateSettings: async (settings) => {
    return await apiClient.put('/api/admin/settings', settings);
  },

  // Password change
  changePassword: async (currentPassword, newPassword) => {
    return await apiClient.put('/api/admin/change-password', {
      currentPassword,
      newPassword
    });
  },
  
  // Authentication
  login: async (username, password) => {
    return await apiClient.post('/api/admin/login', { username, password });
  },

  verifyToken: async () => {
    return await apiClient.get('/api/admin/verify');
  },

  // Customers
  getCustomers: async (params = {}) => {
    return await apiClient.get('/api/admin/customers', { params });
  },

  getCustomerDetails: async (customerId) => {
    return await apiClient.get(`/api/admin/customers/${customerId}`);
  },

  // Per-customer activity timeline
  getCustomerActivity: async (customerId, params = {}) => {
    return await apiClient.get(`/api/admin/customers/${customerId}/activity`, { params });
  },

  // Per-customer shipments (from -> to, status, tracking) + computed risk signals
  getCustomerShipments: async (customerId, params = {}) => {
    return await apiClient.get(`/api/admin/customers/${customerId}/shipments`, { params });
  },

  // Account hold / verification hold (RBAC: support+). Backend reads `reason`.
  holdCustomerAccount: async (customerId, reason) => {
    return await apiClient.post(`/api/admin/customers/${customerId}/hold`, { reason });
  },

  // Voids every still-voidable label on ONE account and sends that customer a single
  // consolidated notice. Moves NO money: the Stripe refund follows per order via
  // refund-with-void once Shippo confirms each void. Pass dryRun to count first.
  voidUnusedLabels: async (email, { dryRun = false } = {}) => {
    return await apiClient.post('/api/admin/customers/void-unused-labels', { email, dryRun });
  },
  unholdCustomerAccount: async (customerId) => {
    return await apiClient.post(`/api/admin/customers/${customerId}/unhold`);
  },

  // Email the customer a high-volume verification request (fraud / chargeback screening),
  // optionally placing a verification hold. Body: { hold, deadlineDays }.
  requestVerification: async (customerId, { hold = true, deadlineDays = 5 } = {}) => {
    return await apiClient.post(`/api/admin/customers/${customerId}/request-verification`, { hold, deadlineDays });
  },

  // Global customer-activity audit feed + available event-type filters
  getAuditLogs: async (params = {}) => {
    return await apiClient.get('/api/admin/audit-logs', { params });
  },

  getAuditLogEventTypes: async () => {
    return await apiClient.get('/api/admin/audit-logs/event-types');
  },

  // Orders
  getOrders: async (params = {}) => {
    return await apiClient.get('/api/admin/orders', { params });
  },

  // Read-only review of purchased labels and their current carrier tracking status.
  getUnusedLabels: async (params = {}) => {
    return await apiClient.get('/api/admin/unused-labels', { params });
  },

  getOrderDetails: async (orderId) => {
    return await apiClient.get(`/api/admin/orders/${orderId}`);
  },

  updateOrderStatus: async (orderId, status) => {
    return await apiClient.patch(`/api/admin/orders/${orderId}/status`, { status });
  },

  // Per-order payment actions (RBAC: capture/void=support+, refund/recharge=finance+)
  captureOrder: async (orderId) => {
    return await apiClient.post(`/api/admin/orders/${orderId}/capture`);
  },

  voidOrder: async (orderId) => {
    return await apiClient.post(`/api/admin/orders/${orderId}/void`);
  },

  refundOrder: async (orderId, amount, reason) => {
    return await apiClient.post(`/api/admin/orders/${orderId}/refund`, { amount, reason });
  },

  rechargeOrder: async (orderId, amount, actionId) => {
    return await apiClient.post(`/api/admin/orders/${orderId}/recharge`, { amount, actionId });
  },

  // Payments / reconciliation
  getReconciliation: async (params = {}) => {
    return await apiClient.get('/api/admin/payments/reconciliation', { params });
  },

  // Refund eligibility + void-refund (RBAC: report/action=finance+, single status=support+)
  getRefundsReady: async (params = {}) => {
    return await apiClient.get('/api/admin/refunds/ready', { params });
  },

  // In-flight refunds (Shippo void initiated, QUEUED/PENDING, not yet paid out).
  // Pass { live: 1 } to re-poll Shippo for each row's current status.
  getPendingRefunds: async (params = {}) => {
    return await apiClient.get('/api/admin/refunds/pending', { params });
  },

  // Zelle wallet funding (store credit). list/reject = support+, approve/adjust = finance+.
  getZelleFundingRequests: async (params = {}) => {
    return await apiClient.get('/api/admin/zelle/funding-requests', { params });
  },
  approveZelleFunding: async (id, note) => {
    return await apiClient.post(`/api/admin/zelle/funding-requests/${id}/approve`, { note });
  },
  rejectZelleFunding: async (id, note) => {
    return await apiClient.post(`/api/admin/zelle/funding-requests/${id}/reject`, { note });
  },
  adjustWallet: async (customerId, amount, reason) => {
    return await apiClient.post(`/api/admin/customers/${customerId}/wallet-adjust`, { amount, reason });
  },

  getShippoRefundStatus: async (orderId) => {
    return await apiClient.get(`/api/admin/orders/${orderId}/shippo-refund-status`);
  },

  // Dry run for bulk void+refund: per-order eligibility, checked against the live Shippo
  // transaction. READ-ONLY — nothing is voided or refunded by this call.
  previewVoidRefund: async (orderIds) => {
    return await apiClient.post('/api/admin/unused-labels/void-refund/preview', { orderIds });
  },
  refundWithVoid: async (orderId, { amount, reason, actionId } = {}) => {
    return await apiClient.post(`/api/admin/orders/${orderId}/refund-with-void`, { amount, reason, actionId });
  },

  // Analytics
  getAnalytics: async (params = {}) => {
    return await apiClient.get('/api/admin/analytics', { params });
  },

  getDashboardStats: async () => {
    return await apiClient.get('/api/admin/dashboard/stats');
  },

  // Shippo Integration
  getShippoTransactions: async (params = {}) => {
    return await apiClient.get('/api/admin/shippo/transactions', { params });
  },

  getShippoTransaction: async (transactionId) => {
    return await apiClient.get(`/api/admin/shippo/transactions/${transactionId}`);
  },

  // Stripe Integration
  getStripeCustomers: async (params = {}) => {
    return await apiClient.get('/api/admin/stripe/customers', { params });
  },

  getStripePayments: async (params = {}) => {
    return await apiClient.get('/api/admin/stripe/payments', { params });
  },

  getStripePayment: async (paymentId) => {
    return await apiClient.get(`/api/admin/stripe/payments/${paymentId}`);
  },

  // Export
  exportCustomers: async (params = {}) => {
    return await apiClient.get('/api/admin/export/customers', { 
      params,
      responseType: 'blob'
    });
  },

  exportOrders: async (params = {}) => {
    return await apiClient.get('/api/admin/export/orders', { 
      params,
      responseType: 'blob'
    });
  },
};

export default apiClient;
